import { API_BASE } from '@/lib/utils';

let configCache = null;

export function clearIdentityConfigCache() {
  configCache = null;
}

export function parseIdentityError(body, status) {
  const detail = body?.detail;
  if (detail && typeof detail === 'object') {
    return {
      code: detail.code || '',
      email: detail.email || '',
      message: detail.message || detail.code || `Request failed (${status})`,
    };
  }
  if (typeof detail === 'string') {
    return { code: '', email: '', message: detail };
  }
  return { code: '', email: '', message: body?.message || `Request failed (${status})` };
}

export async function fetchIdentityConfig({ bustCache = false } = {}) {
  if (bustCache) configCache = null;
  if (configCache) return configCache;
  const res = await fetch(`${API_BASE}/api/auth/identity/config`, { cache: 'no-store' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseIdentityError(data, res.status).message);
  configCache = data;
  return data;
}

export async function verifyIdentity({
  provider,
  idToken,
  intent = 'login',
  orgName = '',
  ownerName = '',
  billingCycle = 'monthly',
  plan = 'studio',
}) {
  const res = await fetch(`${API_BASE}/api/auth/identity/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ provider, idToken, intent, orgName, ownerName, billingCycle, plan }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseIdentityError(data, res.status).message);
    const parsed = parseIdentityError(data, res.status);
    err.code = parsed.code;
    err.email = parsed.email;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function linkIdentity({ provider, idToken }) {
  const { getAuthHeaders } = await import('@/lib/auth-fetch');
  const res = await fetch(`${API_BASE}/api/auth/identity/link`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ provider, idToken }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseIdentityError(data, res.status).message);
  return data;
}

export async function listIdentities() {
  const { getAuthHeaders } = await import('@/lib/auth-fetch');
  const res = await fetch(`${API_BASE}/api/auth/identity`, {
    headers: { ...getAuthHeaders() },
    cache: 'no-store',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseIdentityError(data, res.status).message);
  return data;
}

export async function unlinkIdentity(identityId) {
  const { getAuthHeaders } = await import('@/lib/auth-fetch');
  const res = await fetch(`${API_BASE}/api/auth/identity/${encodeURIComponent(identityId)}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseIdentityError(data, res.status).message);
  return data;
}

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export async function ensureGoogleReady(clientId) {
  if (!clientId) return false;
  await loadScript('https://accounts.google.com/gsi/client', 'google-gsi');
  return Boolean(window.google?.accounts?.id);
}

let googleInitializedKey = '';
let googleTokenHandler = null;

export function initializeGoogleIdentity(container, clientId, callback, intent = 'login') {
  if (!container || !clientId || !window.google?.accounts?.id) return;
  googleTokenHandler = callback;
  const context = intent === 'signup' ? 'signup' : 'signin';
  const initKey = `${clientId}:${context}`;
  if (googleInitializedKey !== initKey) {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response?.credential) googleTokenHandler?.(response.credential);
      },
      context,
      ux_mode: 'popup',
      use_fedcm_for_prompt: false,
    });
    googleInitializedKey = initKey;
  }
  container.innerHTML = '';
  window.google.accounts.id.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: intent === 'signup' ? 'signup_with' : 'signin_with',
    width: 360,
    shape: 'pill',
  });
}

/** @deprecated Prefer initializeGoogleIdentity + custom button UI */
export function renderGoogleButton(container, clientId, callback, intent = 'login') {
  initializeGoogleIdentity(container, clientId, callback, intent);
}

export async function ensureAppleReady(clientId) {
  if (!clientId) return false;
  await loadScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    'apple-auth-js',
  );
  if (!window.AppleID?.auth) return false;
  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI: window.location.origin,
    usePopup: true,
  });
  return true;
}

export async function signInWithApple() {
  const res = await window.AppleID.auth.signIn();
  const idToken = res?.authorization?.id_token;
  if (!idToken) throw new Error('Apple sign-in did not return a token');
  return idToken;
}
